import React, { useState, useEffect, useCallback } from 'react'
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { setUserInfo } from 'app/store/authSlice'
import { setProfile, getProfile } from 'app/store/profileSlice'
import { getJobsByUserAndStatus } from 'app/store/jobSlice'
import { webSocketService } from 'app/services/websocket'
import { employerProfileComplete } from 'app/utils/completeProfile'

const AuthRoutes = () => {
  const [loading, setLoading] = useState(false)
  let user = localStorage.getItem('user')
  const location = useLocation()

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const auth = useSelector((state) => state.auth)
  const profileUser = useSelector((state) => state.profile.user)
  const activeCount = useSelector((state) => state.job.activeCount)
  const inactiveCount = useSelector((state) => state.job.inactiveCount)
  const hasFetchedEmployerJobCount = useSelector(
    (state) => state.job.hasFetchedEmployerJobCount
  )
  const isEmployer = auth?.userType === 'employer'

  const verifyUser = useCallback(async () => {
    user = JSON.parse(user)
    try {
      setLoading(true)
      let result = await getProfile(user.id)
      if (result.status === 200) {
        if (!auth.userId) {
          const payload = {
            id: user.id,
            token: user.token,
            type: user.type,
          }
          dispatch(setUserInfo(payload))
        }

        dispatch(setProfile(result.data.user))
        webSocketService.connect(user.id)

        if (user.type === 'employer') {
          await dispatch(
            getJobsByUserAndStatus({ userId: user.id, status: 'active', limit: 1 })
          )
        }
        setLoading(false)
      }
    } catch (err) {
      // console.log('catched error', err)
      return navigate('/auth')
    }
  }, [])

  useEffect(() => {
    if (user) {
      verifyUser()
    }

    return () => {
      try {
        const parsedUser = typeof user === 'string' ? JSON.parse(user) : user
        webSocketService.disconnect(parsedUser?.id)
      } catch (err) {
        webSocketService.disconnect()
      }
    }
  }, [verifyUser])

  const jobSharedUrlPattern = /^\/jobseeker\/jobs\/([0-9a-fA-F-]{36})$/
  const candidateSharedUrlPattern = /^\/employer\/profile\/([0-9a-fA-F-]{36})$/
  const nonProtectedJobBaseUrl = '/home/job'
  const nonProtectedCandidateBaseUrl = '/home/candidate'

  const jobURLMatch = location.pathname.match(jobSharedUrlPattern)
  const candidateURLMatch = location.pathname.match(candidateSharedUrlPattern)
  // console.log(jobURLMatch)
  // console.log(candidateURLMatch)
  if (!user) {
    const jobURLMatch = location.pathname.match(jobSharedUrlPattern)
    const candidateURLMatch = location.pathname.match(candidateSharedUrlPattern)
    // console.log(jobURLMatch)
    // console.log(candidateURLMatch)
    if (jobURLMatch) {
      const jobId = jobURLMatch[1]
      return <Navigate to={`${nonProtectedJobBaseUrl}/${jobId}`} replace />
    } else if (candidateURLMatch) {
      const candidateId = candidateURLMatch[1]
      return (
        <Navigate
          to={`${nonProtectedCandidateBaseUrl}/${candidateId}`}
          replace
        />
      )
    } else {
      const redirectUrl = location.pathname + location.search
      localStorage.setItem('redirectTo', redirectUrl)
      return (
        <Navigate
          to={`/auth?redirect=${encodeURIComponent(redirectUrl)}`}
          replace
        />
      )
    }
  }

  if (loading) return null

  const isOnEmployerSetupProfilePage = location.pathname.startsWith('/employer/set-up-profile')
  const hasLoadedEmployerProfile = Boolean(profileUser?.id)

  if (isEmployer && !hasLoadedEmployerProfile) return null

  if (
    isEmployer &&
    !isOnEmployerSetupProfilePage &&
    !employerProfileComplete(profileUser)
  ) {
    return <Navigate to='/employer/set-up-profile' replace />
  }

  const employerAllowedPaths = [
    '/employer/add-job-post',
    '/employer/reviewdashplan',
    '/employer/checkout-plan',
  ]
  const isAllowedEmployerPath = employerAllowedPaths.some((path) =>
    location.pathname.startsWith(path)
  )

  if (isEmployer && !hasFetchedEmployerJobCount) return null

  const totalEmployerJobs = (activeCount || 0) + (inactiveCount || 0)

  // comment this out to allow employer to access other paths
  // if (isEmployer && totalEmployerJobs < 1 && !isAllowedEmployerPath) {
  //   return <Navigate to="/employer/add-job-post" replace />
  // }

  return (
    <div>
      <Outlet />
    </div>
  )
}

export default AuthRoutes
